import React from 'react';
import { isBefore } from 'date-fns';
import { FormattedMessage, useIntl } from 'react-intl';

import type { MayBe } from 'types/common';
import { MediaType } from 'types/AbstractMedia';
import { DEFAULT_MINUTES_TO_REOPEN_KIOSK } from 'modules/kiosk/constants';
import { SelfServiceOrigin, SelfServiceType, type SelfService } from 'modules/selfServices/types/SelfService';
import { UPDATE_FIXED_CACHE_KEY } from 'modules/selfServices/constants';
import { NotificationType } from 'modules/notifications/types/Notification';
import type { FinalInstructionsResponse } from 'modules/dealers/types/FinalInstructionsResponse';

import selfServicesApi, { useUpdateSelfServiceMutation } from 'modules/selfServices/service';
import { reopenKiosk } from 'modules/kiosk/actions';
import { addNotification } from 'modules/notifications/actions';

import { getOrigin, isPublicDevice, isRemoteKiosk } from 'modules/dealers/selectors';
import {
  canReopenKiosk,
  getParkingInfo,
  getSelfServiceType,
  getUntilDateToReopenKiosk,
} from 'modules/selfServices/selectors';
import { getVehicleCheckType } from 'modules/vehicleCheck/selectors';
import { getUnreadCountText, haveUnreadMessages } from 'modules/chat/selectors';
import { isReopenKioskLoading as isReopenKioskLoadingSelector } from 'modules/kiosk/selectors';

import { useDispatch, useSelector } from 'hooks';
import { downloadURL } from 'utils/downloadUtils';

import { Icon } from 'components/ui';
import Modal from 'components/ui/Modal';
import ChatModal from 'components/ChatModal';
import Content from 'components/VehicleCheckStep/Content';
import FileDownloadModal from 'components/ui/FileDownloadModal';
import { type ButtonProps } from 'components/ui/Button';
import CourtesyVehicleModal from 'components/CourtesyVehicle/CourtesyVehicleModal';

import ParkingContent from '../Parking/ParkingContent';
import useDownloadFinalInstructionsDocuments from './useDownloadFinalInstructionsDocuments';

type UseFinalInstructionsButtonsParams = {
  selfServiceId: string;
  instructions: MayBe<FinalInstructionsResponse>;
  data: MayBe<SelfService>;
};

export type FinalInstructionButton = Pick<ButtonProps, 'loading' | 'disabled' | 'onClick'> & {
  testId: string;
  label: React.ReactNode;
  addon?: React.ReactNode;
  icon?: React.ReactNode;
};

const useFinalInstructionsButtons = ({
  selfServiceId,
  instructions,
  data,
}: UseFinalInstructionsButtonsParams): FinalInstructionButton[] => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const origin = useSelector(getOrigin);
  const parking = useSelector(getParkingInfo);
  const isRemote = useSelector(isRemoteKiosk);
  const type = useSelector(getSelfServiceType);
  const isPublic = useSelector(isPublicDevice);
  const unreadCount = useSelector(getUnreadCountText);
  const hasUnreadCount = useSelector(haveUnreadMessages);
  const displayReopenKiosk = useSelector(canReopenKiosk);
  const vehicleCheckType = useSelector(getVehicleCheckType);
  const untilDateToReopenKiosk = useSelector(getUntilDateToReopenKiosk);
  const isReopenKioskLoading = useSelector(isReopenKioskLoadingSelector);

  const [, { data: updatedSelfService }] = useUpdateSelfServiceMutation({ fixedCacheKey: UPDATE_FIXED_CACHE_KEY });
  const { documentList, handleDownload, isDownloading } = useDownloadFinalInstructionsDocuments({ selfServiceId, instructions });
  const { qrCodeUrl } = instructions?.loginCodes ?? {};

  const isCheckout = type === SelfServiceType.CHECK_OUT;
  const isCheckin = type === SelfServiceType.CHECK_IN || vehicleCheckType === SelfServiceType.CHECK_IN;
  const hasAcceptedMobility = updatedSelfService?.mobility?.customerAccepted ?? data?.mobility?.customerAccepted;

  const displayCourtesy = isCheckin && Boolean(data?.mobility?.vehicle && hasAcceptedMobility);
  const displayChat = instructions?.isChatEnabled && (origin === SelfServiceOrigin.HOME || isRemote || !isPublic);
  const displayParkingLocation = Boolean(parking?.spotNumber || parking?.x >= 0 || parking?.y >= 0) && isCheckout;
  const displayDownloadButtons = (!isPublic || isRemote) && (!isCheckout || documentList.length > 0);
  const displayDownloadDocuments = displayDownloadButtons && documentList.length > 1;
  const displayDownloadSingleDocument = displayDownloadButtons && documentList.length === 1;

  const invoiceList = React.useMemo(() => data?.finalInvoice?.medias ?? [], [data]);
  const displayFinalInvoice = !isPublic && isCheckout && invoiceList.length > 0;
  const displayFinalInvoiceDocuments = displayFinalInvoice && invoiceList.length > 1;
  const displayFinalInvoiceSingleDocument = displayFinalInvoice && invoiceList.length === 1;

  const [isChatModalOpen, setIsChatModalOpen] = React.useState(false);
  const [isParkingModalOpen, setIsParkingModalOpen] = React.useState(false);
  const [isCourtesyModalOpen, setIsCourtesyModalOpen] = React.useState(false);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = React.useState(false);
  const [isInvoiceModalOpen, setIsInvoiceModalOpen] = React.useState(false);
  const [isVehicleCheckModalOpen, setIsVehicleCheckModalOpen] = React.useState(false);

  const handleShowChat = React.useCallback(() => setIsChatModalOpen(true), []);
  const handleShowParking = React.useCallback(() => setIsParkingModalOpen(true), []);
  const handleShowCourtesy = React.useCallback(() => setIsCourtesyModalOpen(true), []);
  const handleShowDownload = React.useCallback(() => setIsDownloadModalOpen(true), []);
  const handleShowInvoices = React.useCallback(() => setIsInvoiceModalOpen(true), []);
  const handleShowVehicleCheck = React.useCallback(() => setIsVehicleCheckModalOpen(true), []);

  const handleCancelVehicleCheck = React.useCallback(async () => {
    await dispatch(selfServicesApi.util.updateQueryData('getFinalInstructions', { selfServiceId }, (draft: FinalInstructionsResponse) => {
      draft.courtesyVehicleCheckAccepted = false;
      draft.vehicleCheckAccepted = false;
      draft.vehicleCheckLink = undefined;
      draft.vehicleCheckLinkExpiredAt = undefined;
    }));
    setIsVehicleCheckModalOpen(false);
  }, [dispatch, selfServiceId]);

  const handleReopen = React.useCallback(() => {
    if (!untilDateToReopenKiosk || isBefore(Date.now(), untilDateToReopenKiosk)) {
      dispatch(reopenKiosk());
    } else {
      dispatch(
        addNotification({
          title: {
            id: 'page.finalInstructions.forbiddenReopen.title',
            defaultMessage: 'The kiosk cannot be reopened anymore',
          },
          description: {
            id: 'page.finalInstructions.forbiddenReopen.description',
            defaultMessage: '{minutes} minutes have passed since you completed the self-service.',
            values: {
              minutes: DEFAULT_MINUTES_TO_REOPEN_KIOSK,
            },
          },
          type: NotificationType.ERROR,
        }),
      );
    }
  }, [dispatch, untilDateToReopenKiosk]);

  return React.useMemo(() => [
    displayParkingLocation && {
      testId: 'showParking',
      onClick: handleShowParking,
      label: (
        <span className="truncate flex-1 text-left text-s!">
          <FormattedMessage id="page.finalInstructions.parking" defaultMessage="Show my parking location" />
        </span>
      ),
      addon: (
        <Modal open={isParkingModalOpen} onClose={setIsParkingModalOpen} className="modal-large">
          <div className="content pb-0">
            <ParkingContent hideInstructions maps={data?.parkingMap?.maps} {...data?.parking} />
          </div>
        </Modal>
      ),
    },
    displayCourtesy && {
      testId: 'showCourtesy',
      onClick: handleShowCourtesy,
      label: (
        <span className="truncate flex-1 text-left text-s!">
          <FormattedMessage id="courtesyVehicle.title" defaultMessage="Courtesy vehicle" />
        </span>
      ),
      addon: <CourtesyVehicleModal open={isCourtesyModalOpen} onClose={setIsCourtesyModalOpen} />,
    },
    displayChat && {
      testId: 'showChat',
      onClick: handleShowChat,
      label: (
        <div className="flex-1 overflow-hidden">
          <div className="items-center justify-between gap-2 flex flex-wrap">
            <span className="truncate flex-1 text-left text-default text-s!">
              <FormattedMessage id="page.finalInstructions.chat" defaultMessage="Chat with the dealership" />
            </span>
            {hasUnreadCount && (
              // A circle for a single digit, a pill once the count needs more room ("99+")
              <span
                className="flex shrink-0 items-center justify-center h-[2em] min-w-[2em] px-3xs rounded-full bg-brand-default text-white text-xxs! leading-none!"
                aria-label={
                  intl.formatMessage(
                    { id: 'accessibility.button.newMessages', defaultMessage: '{count, select, 1 {1 new message} other {{count} new messages}}' },
                    { count: unreadCount },
                  )}
              >
                {unreadCount}
              </span>
            )}
          </div>
        </div>
      ),
      addon: (
        <ChatModal
          open={isChatModalOpen}
          onClose={setIsChatModalOpen}
          selfServiceId={selfServiceId}
          maxMessageLength={instructions.maxMessageLength}
        />
      ),
    },
    displayDownloadSingleDocument && {
      loading: isDownloading,
      disabled: isDownloading,
      testId: 'downloadDocument',
      onClick: () => handleDownload(documentList[0]),
      label: (
        <span className="truncate flex-1 text-left text-s!">
          {documentList[0].filename}
        </span>
      ),
    },
    displayDownloadDocuments && {
      testId: 'downloadDocuments',
      onClick: handleShowDownload,
      label: (
        <span className="truncate flex-1 text-left text-s!">
          <FormattedMessage id="page.finalInstructions.downloadDocuments" defaultMessage="Download documents" />
        </span>
      ),
      addon: (
        <FileDownloadModal
          files={documentList}
          open={isDownloadModalOpen}
          onDownload={handleDownload}
          onClose={setIsDownloadModalOpen}
        />
      ),
    },
    displayReopenKiosk && {
      onClick: handleReopen,
      testId: 'reopenKiosk',
      loading: isReopenKioskLoading,
      disabled: isReopenKioskLoading,
      label: (
        <span className="truncate flex-1 text-left text-s!">
          <FormattedMessage id="page.finalInstructions.reopen" defaultMessage="Reopen the kiosk" />
        </span>
      ),
    },
    displayFinalInvoiceDocuments && {
      testId: 'downloadInvoices',
      onClick: handleShowInvoices,
      label: (
        <span className="truncate flex-1 text-left text-s!">
          <FormattedMessage id="page.finalInstructions.documents" defaultMessage="Final invoice documents" />
        </span>
      ),
      addon: (
        <FileDownloadModal
          files={invoiceList}
          open={isInvoiceModalOpen}
          onClose={setIsInvoiceModalOpen}
          title={(
            <FormattedMessage
              id="page.finalInstructions.documents"
              defaultMessage="Final invoice documents"
              tagName="h3"
            />
          )}
        />
      ),
    },
    displayFinalInvoiceSingleDocument && {
      testId: 'downloadInvoice',
      onClick: () => downloadURL(invoiceList[0].path, invoiceList[0].filename),
      label: (
        <span className="truncate flex-1 text-left text-s!">
          <FormattedMessage id="page.finalInvoice.buttons.download" defaultMessage="Download invoice" />
        </span>
      ),
    },
    instructions?.vehicleCheckLink && {
      onClick: handleShowVehicleCheck,
      testId: 'vehicleCheckInfo',
      label: (
        <span className="truncate flex-1 text-left text-s!">
          <FormattedMessage id="page.finalInstructions.vehicleCheckInfo" defaultMessage="Vehicle check information" />
        </span>
      ),
      addon: (
        <Modal
          open={isVehicleCheckModalOpen}
          onClose={setIsVehicleCheckModalOpen}
          className="modal-large"
          title={
            <FormattedMessage
              id="page.vehicleCheck.qrCodeStep.title"
              defaultMessage="Guided vehicle check"
              tagName="h2"
            />
          }
        >
          <div className="flex flex-col items-center justify-center gap-4 kiosk:gap-8 text-center w-full">
            <Content
              onClose={handleCancelVehicleCheck}
              {...instructions}
            />
          </div>
        </Modal>
      ),
    },
    qrCodeUrl && {
      onClick: () => handleDownload({ path: qrCodeUrl, type: MediaType.IMAGE }),
      label: (
        <span className="truncate flex-1 text-left text-s!">
          <FormattedMessage id="page.finalInstructions.downloadQrCode" defaultMessage="Download QR code to scan at the kiosk reader" />
        </span>
      ),
      icon: <Icon className="fb-icon--download text-2xl" />,
      testId: 'downloadQRCode',
    },
  ].filter(Boolean) as FinalInstructionButton[], [
    displayParkingLocation, handleShowParking, isParkingModalOpen, data,
    displayCourtesy, handleShowCourtesy, isCourtesyModalOpen,
    displayChat, handleShowChat, isChatModalOpen, hasUnreadCount, unreadCount, intl, selfServiceId, instructions,
    displayDownloadSingleDocument, isDownloading, documentList, handleDownload,
    displayDownloadDocuments, handleShowDownload, isDownloadModalOpen,
    displayReopenKiosk, handleReopen, isReopenKioskLoading,
    displayFinalInvoiceSingleDocument, invoiceList,
    displayFinalInvoiceDocuments, handleShowInvoices, isInvoiceModalOpen,
    handleShowVehicleCheck, isVehicleCheckModalOpen, handleCancelVehicleCheck,
    qrCodeUrl,
  ]);
};

export default useFinalInstructionsButtons;
